import React, { useEffect, useMemo, useState } from 'react';
import { Archive, ArrowRight, KeyRound, Plus, Save, SquareStack, Users } from 'lucide-react';
import { Sizes } from 'enums/sizes';
import { Button } from 'components/ui/Button';
import { InputText, Checkbox } from 'components/forms';
import { usePanel } from 'hooks/usePanel';
import { atelierRolesAPI } from 'services/swagger-api';
import {
  BriqueReferentielDto,
  GetBriquePermissionsResponse,
  GetReferentielBriquesResponse
} from 'api/atelier-roles';
import { portailLabel } from 'core/roles';
import { BriqueTypeBadge, InlineError, PortailChip, runOperation } from 'back-office/shared/components/rolesUi';
import { CreateBriqueForm } from './CreateBriqueForm';
import { SectionHeader } from './SectionHeader';

interface RolesTabProps {
  referentiel: GetReferentielBriquesResponse;
  onChanged: () => Promise<void>;
}

/**
 * Référentiel des briques en maître/détail : la liste, puis au clic la fiche
 * d'une brique en pleine largeur (permissions groupées). Les standard sont
 * verrouillées (pilotées par le code, duplicables jamais éditables), les
 * personnalisées s'ajustent et s'archivent si plus aucun porteur.
 */
export const RolesTab: React.FC<RolesTabProps> = ({ referentiel, onChanged }) => {
  const { openPanel } = usePanel();
  const [selectedNom, setSelectedNom] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  // Recherche client (petit référentiel) : nom, description et portails.
  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return referentiel.briques;
    return referentiel.briques.filter(
      (b) =>
        b.nom.toLowerCase().includes(term) ||
        (b.description ?? '').toLowerCase().includes(term) ||
        b.portails.some((portail) => portailLabel(portail).toLowerCase().includes(term))
    );
  }, [referentiel.briques, search]);

  // Dérivée du référentiel pour rester fraîche après un reload ; si la brique
  // a disparu (archivée), on retombe sur la liste.
  const selected = selectedNom
    ? referentiel.briques.find((b) => b.nom === selectedNom) ?? null
    : null;

  if (selected) {
    return (
      <BriqueDetail
        key={selected.nom}
        brique={selected}
        referentiel={referentiel}
        onChanged={onChanged}
        onBack={() => setSelectedNom(null)}
      />
    );
  }

  return (
    <div className="space-y-6">
      <SectionHeader
        icon={SquareStack}
        title="Rôles"
        subtitle="Les briques standard sont pilotées par le code — dupliquez-en une pour créer une variante."
        search={{ value: search, onChange: setSearch, placeholder: 'Rechercher...' }}
        actions={
          <Button
            color="primary"
            round
            onClick={() =>
              openPanel(
                { title: 'Nouvelle brique', size: Sizes['2xl'] },
                <CreateBriqueForm referentiel={referentiel} onSuccess={onChanged} />
              )
            }
          >
            <Plus data-slot="icon" /> Nouvelle brique
          </Button>
        }
      />

      <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
        {filtered.map((brique) => (
          <div
            key={brique.nom}
            className="flex flex-col border rounded-xl bg-white shadow-sm overflow-hidden"
          >
            <header className="flex items-center justify-between gap-3 bg-primary px-6 py-4">
              <h3 className="truncate text-base font-semibold text-white">{brique.nom}</h3>
              <BriqueTypeBadge estStandard={brique.estStandard} />
            </header>
            <div className="flex flex-1 flex-col gap-3 px-6 py-4">
              {brique.description && <p className="text-xs text-gray-500">{brique.description}</p>}
              <div className="flex flex-wrap gap-1">
                {brique.portails.length === 0 ? (
                  <span className="text-xs italic text-amber-600">Aucun portail (orpheline)</span>
                ) : (
                  brique.portails.map((p) => <PortailChip key={p} portail={p} />)
                )}
              </div>
              <div className="flex items-center gap-4 pt-1 text-xs text-gray-500">
                <span className="inline-flex items-center gap-1.5">
                  <KeyRound className="h-3.5 w-3.5 text-gray-400" />
                  <span className="tabular-nums font-medium text-gray-700">{brique.nbPermissions}</span>
                  permissions
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <Users className="h-3.5 w-3.5 text-gray-400" />
                  <span className="tabular-nums font-medium text-gray-700">{brique.nbPorteurs}</span>
                  porteurs
                </span>
              </div>
              <div className="mt-auto border-t pt-3">
                <button
                  type="button"
                  onClick={() => setSelectedNom(brique.nom)}
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded"
                >
                  Voir les permissions <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filtered.length === 0 && (
        <p className="py-8 text-center text-sm text-gray-500">Aucun rôle ne correspond à la recherche.</p>
      )}
    </div>
  );
};

const BriqueDetail: React.FC<{
  brique: BriqueReferentielDto;
  referentiel: GetReferentielBriquesResponse;
  onChanged: () => Promise<void>;
  onBack: () => void;
}> = ({ brique, referentiel, onChanged, onBack }) => {
  const [permissions, setPermissions] = useState<GetBriquePermissionsResponse | null>(null);
  const [granted, setGranted] = useState<Set<string>>(new Set());
  // Photo des permissions accordées au chargement : sert au tri « accordées
  // d'abord » sans que les cases se réordonnent pendant l'édition.
  const [initialGranted, setInitialGranted] = useState<Set<string>>(new Set());
  const [description, setDescription] = useState(brique.description ?? '');
  const [portails, setPortails] = useState<string[]>(brique.portails);
  const [search, setSearch] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    atelierRolesAPI
      .getBriquePermissions({ nom: brique.nom })
      .then((res) => {
        if (cancelled) return;
        setPermissions(res);
        const accordees = new Set(
          res.groupes.flatMap((g) => g.permissions.filter((p) => p.accordee).map((p) => p.nom))
        );
        setGranted(accordees);
        setInitialGranted(accordees);
      })
      .catch((err) => {
        console.error('Erreur de chargement des permissions:', err);
        if (!cancelled) setError('Impossible de charger les permissions de cette brique.');
      });
    return () => {
      cancelled = true;
    };
  }, [brique.nom]);

  // Permissions accordées (au chargement) en premier — dans chaque groupe, puis
  // entre groupes : ceux qui portent des permissions accordées remontent.
  const filteredGroupes = useMemo(() => {
    if (!permissions) return [];
    const term = search.trim().toLowerCase();
    const nbAccordees = (perms: { nom: string }[]) =>
      perms.filter((p) => initialGranted.has(p.nom)).length;
    return permissions.groupes
      .map((g) => ({
        ...g,
        permissions: [...g.permissions]
          .filter(
            (p) =>
              !term ||
              p.nom.toLowerCase().includes(term) ||
              (p.libelle ?? '').toLowerCase().includes(term)
          )
          .sort(
            (a, b) => Number(initialGranted.has(b.nom)) - Number(initialGranted.has(a.nom))
          )
      }))
      .filter((g) => g.permissions.length > 0)
      .sort((a, b) => Number(nbAccordees(b.permissions) > 0) - Number(nbAccordees(a.permissions) > 0));
  }, [permissions, search, initialGranted]);

  const togglePermission = (nom: string, checked: boolean) => {
    setGranted((prev) => {
      const next = new Set(prev);
      if (checked) next.add(nom);
      else next.delete(nom);
      return next;
    });
  };

  const saveAll = async () => {
    setIsSaving(true);
    let err = await runOperation(() =>
      atelierRolesAPI.updateBrique({
        nom: brique.nom,
        updateBriqueCommand: { description: description.trim() || null, portails }
      })
    );
    if (!err) {
      err = await runOperation(() =>
        atelierRolesAPI.setBriquePermissions({
          nom: brique.nom,
          setBriquePermissionsCommand: { permissions: Array.from(granted) }
        })
      );
    }
    setIsSaving(false);
    setError(err);
    if (!err) {
      // Le nouvel état accordé devient la référence du tri « accordées d'abord ».
      setInitialGranted(new Set(granted));
      await onChanged();
    }
  };

  const archive = async () => {
    if (!window.confirm(`Archiver la brique « ${brique.nom} » ? Elle ne sera plus attribuable.`)) return;
    setIsSaving(true);
    const err = await runOperation(() => atelierRolesAPI.archiverBrique({ nom: brique.nom }));
    setIsSaving(false);
    setError(err);
    if (!err) {
      await onChanged();
      onBack();
    }
  };

  const readOnly = brique.estStandard;

  return (
    <div className="space-y-4">
      <SectionHeader
        icon={SquareStack}
        title="Rôles"
        onTitleClick={onBack}
        breadcrumb={
          <>
            <span className="text-gray-300">/</span>
            <span className="font-semibold text-gray-900">{brique.nom}</span>
            <BriqueTypeBadge estStandard={brique.estStandard} />
          </>
        }
        subtitle={`${granted.size} permissions accordées`}
        search={{ value: search, onChange: setSearch, placeholder: 'Filtrer les permissions…' }}
        actions={
          !readOnly && (
            <>
              <Button
                color="red"
                disabled={isSaving || brique.nbPorteurs > 0}
                tooltip={
                  brique.nbPorteurs > 0
                    ? `${brique.nbPorteurs} porteurs actifs — retirez-la d'abord de leurs comptes.`
                    : undefined
                }
                onClick={archive}
              >
                <Archive data-slot="icon" /> Archiver
              </Button>
              <Button color="primary" disabled={isSaving || !permissions} onClick={saveAll}>
                <Save data-slot="icon" /> Enregistrer
              </Button>
            </>
          )
        }
      />

      <InlineError message={error} />

      {!readOnly && (
        <div className="grid gap-3 md:grid-cols-2">
          <InputText
            label="Description"
            name={`description-${brique.nom}`}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
          <div>
            <span className="mb-1 block text-sm font-medium text-gray-700">Portails</span>
            <div className="flex flex-wrap gap-4 pt-1">
              {referentiel.portails.map((portail) => (
                <Checkbox
                  key={portail}
                  name={`portail-${brique.nom}-${portail}`}
                  label={portailLabel(portail)}
                  checked={portails.includes(portail)}
                  onChange={(checked) =>
                    setPortails((prev) =>
                      checked ? [...prev, portail] : prev.filter((p) => p !== portail)
                    )
                  }
                />
              ))}
            </div>
          </div>
        </div>
      )}

      {!permissions && !error && <p className="text-sm text-gray-500">Chargement des permissions…</p>}

      {/* Masonry par colonnes CSS : les groupes ont des tailles très inégales,
          une grid stricte laisserait de grands trous à côté des gros groupes. */}
      <div className="columns-1 lg:columns-2 2xl:columns-3 gap-4 [&>*]:mb-4">
        {filteredGroupes.map((groupe) => (
          <div key={groupe.nom} className="break-inside-avoid border rounded-xl bg-white shadow-sm overflow-hidden">
            <div className="flex items-center justify-between gap-3 px-6 py-3 bg-primary/5 border-b">
              <h4 className="text-sm font-semibold text-gray-800">{groupe.libelle ?? groupe.nom}</h4>
              <span className="text-xs tabular-nums text-gray-500">
                {groupe.permissions.filter((p) => granted.has(p.nom)).length}/{groupe.permissions.length}
              </span>
            </div>
            <div className="grid gap-1.5 px-6 py-4 md:grid-cols-2">
              {groupe.permissions.map((perm) => (
                <Checkbox
                  key={perm.nom}
                  name={`perm-${brique.nom}-${perm.nom}`}
                  label={perm.libelle ?? perm.nom}
                  disabled={readOnly}
                  checked={granted.has(perm.nom)}
                  onChange={(checked) => togglePermission(perm.nom, checked)}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
